import { NavLink, useParams, useNavigate, useLocation } from "react-router";
import {
  LayoutDashboard,
  Users,
  ListChecks,
  BookOpen,
  CalendarDays,
  MessageCircle,
  ArrowLeft,
  UserCog,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect } from "react";

const LAST_CLIENT_KEY = "admin_last_client_id";

interface BottomBarProps {
  role: "admin" | "client";
  jwt: string;
}

export function BottomBar({ role, jwt }: BottomBarProps) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Remember the last visited clientId so the main nav can go back to them
  useEffect(() => {
    if (role === "admin" && clientId) {
      sessionStorage.setItem(LAST_CLIENT_KEY, clientId);
    }
  }, [role, clientId]);

  const lastClientId = sessionStorage.getItem(LAST_CLIENT_KEY);

  const adminItemsWithClient = clientId
    ? [
        { to: `/admin/${jwt}/${clientId}/chores`, icon: ListChecks, label: "Chores" },
        { to: `/admin/${jwt}/${clientId}/rules`, icon: BookOpen, label: "Rules" },
        { to: `/admin/${jwt}/${clientId}/sessions`, icon: CalendarDays, label: "Sessions" },
        { to: `/admin/${jwt}/${clientId}/messages`, icon: MessageCircle, label: "Chat" },
      ]
    : null;

  const adminItemsNoClient = [
    { to: `/admin/${jwt}/dashboard`, icon: LayoutDashboard, label: "Home" },
    { to: `/admin/${jwt}/clients`, icon: Users, label: "Partners" },
  ];

  const clientItems = [
    { to: `/client/${jwt}/dashboard`, icon: LayoutDashboard, label: "Home" },
    { to: `/client/${jwt}/chores`, icon: ListChecks, label: "Chores" },
    { to: `/client/${jwt}/rules`, icon: BookOpen, label: "Rules" },
    { to: `/client/${jwt}/sessions`, icon: CalendarDays, label: "Sessions" },
    { to: `/client/${jwt}/messages`, icon: MessageCircle, label: "Chat" },
    { to: `/client/${jwt}/profile`, icon: UserCog, label: "Profile" },
  ];

  const items = role === "admin"
    ? (adminItemsWithClient || adminItemsNoClient)
    : clientItems;

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-app-border bg-app-surface/95 backdrop-blur-sm">
      <div className="flex items-center justify-around py-1 pb-[env(safe-area-inset-bottom)]">
        {/* Client context: Dashboard button goes to client manage page */}
        {role === "admin" && clientId && (
          <button
            onClick={() => navigate(`/admin/${jwt}/${clientId}`)}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors min-w-12 text-app-text-muted active:text-app-primary"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span className="text-[10px] leading-tight">Overview</span>
          </button>
        )}
        {/* Main nav (no client): Back button returns to last viewed client */}
        {role === "admin" && !clientId && lastClientId && (
          <button
            onClick={() => navigate(`/admin/${jwt}/${lastClientId}`)}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors min-w-12 text-app-text-muted active:text-app-primary"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-[10px] leading-tight">Back</span>
          </button>
        )}
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors min-w-12",
                isActive
                  ? "text-app-primary"
                  : "text-app-text-muted"
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] leading-tight">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
